/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import _mergeWith from "lodash/mergeWith";
import _isEqual from "lodash/isEqual";

type MergeCustomizer = Parameters<typeof _mergeWith>[2];

/**
 * Merges source entities into the target state, but ONLY for entities that are actually different.
 * Entities that are deeply equal to their existing values are skipped entirely,
 * preserving their object references and preventing unnecessary re-renders.
 *
 * @param state - The current state object (e.g., entities.sections.objects)
 * @param source - The incoming entities to merge (e.g., action.payload.entities.sections)
 * @param customizer - Optional lodash mergeWith customizer function for custom merge behavior
 * @returns The state (possibly mutated if there were actual changes)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mergeEntitiesIfChanged<T extends Record<string, any>>(
  state: T,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  source: any,
  customizer?: MergeCustomizer
): T {
  if (!source) return state;

  // Build a filtered source containing only entities that have actually changed
  // This is the key insight: we only merge entities that are new or different,
  // leaving unchanged entities with their original object references
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const changedEntities: Record<string, any> = {};

  for (const key of Object.keys(source)) {
    const existingValue = state[key];
    const newValue = source[key];

    // If the key doesn't exist in state, we need to add it
    if (existingValue === undefined) {
      changedEntities[key] = newValue;
    }

    // Only include entities that are actually different
    else if (!_isEqual(existingValue, newValue)) {
      changedEntities[key] = newValue;
    }
  }

  // Only merge if there are actual changes
  if (Object.keys(changedEntities).length) {
    return _mergeWith(state, changedEntities, customizer);
  }
  return state;
}

/**
 * Helper to extract entities from an action's response or payload.
 * Works with both old-style actions (response.entities) and new-style (payload.entities).
 *
 * @param action - The Redux action
 * @param entityName - The name of the entity to extract (e.g., "kits", "sections")
 * @returns The entities object or undefined if not found
 */
export function getEntitiesFromAction<T = unknown>(
  action: unknown,
  entityName: string
): Record<string, T> | undefined {
  try {
    const act = action as {
      response?: { entities?: Record<string, Record<string, T>> };
      payload?: { entities?: Record<string, Record<string, T>> };
    };
    return act?.response?.entities?.[entityName] || act?.payload?.entities?.[entityName];
  } catch {
    return undefined;
  }
}
